import './index.scss';
import './reactCrop.scss';
import { lang } from '@umu/ukit-lang';
import domToImage from 'dom-to-image-more';
import { isSafari } from '@umu/umu-ua';
import html2canvas from 'html2canvas';
import { Button } from '@umu/umu-design';
import If from 'common/src/components/if';
import { EMediaType } from '@umu/umu-types';
import { getUploadResult } from '../../services';
import * as ImageUtil from '@umu/umu-image-handle';
import { dealCheckVal } from '../uploadImageAndCrop/util';
import { $document, classSet, $window } from '@umu/umu-dom';
import { UploadLoading } from '../@uploadLoading/uploadLoading';
import ReactCrop, { Crop, PercentCrop } from 'react-image-crop';
import { SOURCE_IMG_TYPE } from '../uploadImageAndCrop/interface';
import React, { useCallback, useRef, useState, useEffect } from 'react';
import { checkIsAuthCDNUrl } from '@umu/umu-util';

interface IProps {
    src: string;
    cropRatio: number;
    teacherId: string;
    panelTitle?: string;
    uploadLoadText: string;
    multiple?: number; // 裁切倍数
    sourceImgType: SOURCE_IMG_TYPE;
    isShowToolBar?: boolean; // 是否显示工具栏
    onErr(): void;
    onCancel(): void;
    onChangeImg(): void;
    onConfirm(data: string): void;
}

enum OPERATE_TYPE {
    ROTATE,
    ZOOM_IN,
    ZOOM_OUT,
}
const pixelRatioDefault = $window?.devicePixelRatio || 1;

const isDifferenceInRange = (num1: number, num2: number, range: number, fixedCOunt = 2): boolean => {
    const difference = Math.abs(num1 - num2).toFixed(fixedCOunt);
    return Number(difference) <= range;
};

export const ImgCropPortal = (props: IProps) => {
    const {
        src,
        teacherId,
        cropRatio = 16 / 9,
        multiple = 1,
        isShowToolBar = false,
        uploadLoadText,
        panelTitle,
        onCancel,
        onConfirm,
        onErr,
        onChangeImg,
    } = props;
    const imgRef = useRef<HTMLImageElement>();
    const canvasRef = useRef<HTMLCanvasElement>();
    const [disabled, setDisabled] = useState(true);
    const [imgUrl, setImgUrl] = useState(src || '');
    const [isSession, setIsSession] = useState(false);
    const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
    const [crop, setCrop] = useState<Crop>({ unit: '%', width: 100, aspect: cropRatio });
    const onLoadErr = useCallback(() => {
        const newUrl = ImageUtil.appendWidth(imgUrl, 256);
        imgUrl !== newUrl && setImgUrl(newUrl);
    }, []);
    const onLoad = useCallback((img: any) => {
        imgRef.current = img;
        const canvas = $document.createElement('canvas');
        canvasRef.current = canvas;
        setDisabled(false);
    }, []);
    useEffect(() => {
        if (props.sourceImgType === SOURCE_IMG_TYPE.SESSION) {
            setIsSession(true);
            init();
        }
    }, [props.sourceImgType]);
    const getSessionImg = async (ele: HTMLElement) => {
        if (isSafari) {
            // Safari浏览器使用html2canvas
            const canvas = await html2canvas(ele, {
                useCORS: true,
                allowTaint: true,
                backgroundColor: null,
            });
            return canvas.toDataURL('image/png');
        } else {
            const domToImageOptions: Record<string, boolean> = {};
            if (checkIsAuthCDNUrl(ele?.outerHTML)) {
                domToImageOptions.useCredentials = true;
            }
            // 其他浏览器使用domToImage
            return await domToImage.toPng(ele);
        }
    };
    const init = async () => {
        const elementId = 'imgSessionImg';
        const ele = $document.getElementById(elementId);

        if (ele) {
            ele.style.display = 'table-cell';
            ele.style.width = `${4 * cropRatio}rem`;
            ele.style.height = '4rem';
            try {
                const newSrc = await getSessionImg(ele);
                ele.style.display = 'none';
                setImgUrl(newSrc);
                setIsSession(false);
            } catch (error) {
                console.error('[ImgCropPortal] init error: ', error);
                // todo: 需要重构，onErr 需要传入错误信息
                // onErr();
            }
        }
    };
    const onCropChange = (newCrop: Crop, _percentCrop: PercentCrop) => {
        setCrop(newCrop);
    };

    const onCropComplete = (newCrop: Crop, _percentCrop: PercentCrop) => {
        setCompletedCrop(newCrop);
    };

    const blobBack = (blob: Blob | null) => {
        if (!blob) {
            UploadLoading.hide();
            onErr();
            return;
        }
        const previewUrl = $window.URL.createObjectURL(blob);
        // 浏览器兼容
        const param = {
            blob,
            ext: 'png',
            previewUrl,
            fileName: '',
            mediaType: EMediaType.IMAGE,
            teacherId,
        };
        getUploadResult(param)
            .then((payloadData) => {
                setDisabled(false);
                UploadLoading.hide();
                $window.URL.revokeObjectURL(previewUrl);
                onConfirm(payloadData.url);
                onCancel();
            })
            .catch(() => {
                setDisabled(false);
                UploadLoading.hide();
                $window.URL.revokeObjectURL(previewUrl);
                onErr();
            });
    };

    const confirmHandle = () => {
        const image = imgRef.current!;
        const canvas = canvasRef.current!;
        const completedCropWidth = completedCrop?.width || 200;
        if (!completedCrop || disabled) {
            return;
        }
        const imageRatio = Number((image.width / image.height).toFixed(2));
        const cropRatioTmp = Number(props.cropRatio.toFixed(2));
        /*
        如果裁剪比例与要求比例相同且宽度差值为5范围内，则直接返回图片链接
        1、节省不必要裁剪，差值5是去除边框后的误差范围
        2、裁剪比例精确到百分位
        */
        if (
            isDifferenceInRange(imageRatio, cropRatioTmp, 0.01) &&
            isDifferenceInRange(completedCropWidth, image.width, 5)
        ) {
            if (!/blob/.test(imgUrl) && dealCheckVal(imgUrl)) {
                onConfirm(imgUrl);
                onCancel();
            } else if (/blob/.test(imgUrl)) {
                fetch(imgUrl).then(async (res) => {
                    blobBack(await res.blob());
                });
            }
            return;
        }
        setDisabled(true);
        const pixelRatio = pixelRatioDefault * multiple;
        UploadLoading.show({ message: uploadLoadText });

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const completedCropHeight = completedCrop?.height || completedCropWidth / cropRatio;

        canvas.width = completedCropWidth * pixelRatio;
        canvas.height = completedCropHeight * pixelRatio;

        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

        const img = document.createElement('img');
        const offsetX = completedCrop.x! * scaleX;
        const offsetY = completedCrop.y! * scaleY;

        let url = imgUrl;
        if (!/blob/.test(imgUrl) && dealCheckVal(imgUrl) && props.sourceImgType !== SOURCE_IMG_TYPE.SESSION) {
            url = `${imgUrl}${/\?/.test(imgUrl) ? '&' : '?'}t=${Date.now()}`;
        }

        img.src = url;

        img.crossOrigin = 'anonymous';
        img.onload = () => {
            setTimeout(() => {
                ctx.drawImage(
                    img,
                    offsetX,
                    offsetY,
                    completedCropWidth * scaleX,
                    completedCropHeight * scaleY,
                    0,
                    0,
                    completedCropWidth,
                    completedCropHeight,
                );
                canvas.toBlob(blobBack, 'image/png', 1);
            }, 1000);
        };

        img.onerror = () => {
            setDisabled(false);
            UploadLoading.hide();
            onErr();
        };
    };
    /**
     * 图片工具栏操作,功能尚未开放
     */
    const onImageToolBar = (type: OPERATE_TYPE) => {
        if (disabled) {
            return;
        }
        const newCrop = { ...crop };
        switch (type) {
            case OPERATE_TYPE.ROTATE: // 旋转
                imgRef.current!.style.transform = `rotate(${
                    (imgRef.current!.style.transform
                        ? parseInt(imgRef.current!.style.transform.replace(/[^0-9]/gi, ''), 10)
                        : 0) + 90
                }deg)`;
                break;
            case OPERATE_TYPE.ZOOM_IN: // 放大
                imgRef.current!.style.width = `${
                    (imgRef.current!.style.width
                        ? parseInt(imgRef.current!.style.width.replace(/[^0-9]/gi, ''), 10)
                        : 100) - 10
                }%`;
                break;
            case OPERATE_TYPE.ZOOM_OUT: // 缩小
                imgRef.current!.style.width = `${
                    (imgRef.current!.style.width
                        ? parseInt(imgRef.current!.style.width.replace(/[^0-9]/gi, ''), 10)
                        : 100) + 10
                }%`;
                break;
            default:
                break;
        }
        setCrop(newCrop);
    };
    const render = () => {
        return (
            <div className="course-crop-portal business-common-course-crop-portal">
                {panelTitle && <div className="course-crop-portal-heard">{panelTitle}</div>}
                <i
                    id="imgSessionImg"
                    style={{
                        alignItems: 'center',
                        display: 'none',
                        margin: 'auto',
                        fontSize: '90px',
                        verticalAlign: 'middle',
                        textAlign: 'center',
                    }}
                    className={!isSession ? '' : imgUrl}
                />
                <If condition={!isSession}>
                    <div
                        className={classSet({
                            'crop-area': true,
                            'crop-area-avatar-img': props.sourceImgType === SOURCE_IMG_TYPE.AVATAR,
                        })}
                    >
                        <ReactCrop
                            src={imgUrl}
                            crop={crop}
                            keepSelection={true}
                            onImageLoaded={onLoad}
                            onImageError={onLoadErr}
                            onChange={onCropChange}
                            onComplete={onCropComplete}
                        ></ReactCrop>
                        <div className="react-crop-img-micro react-crop-img-micro-background"></div>
                        <div className="react-crop-img-micro react-crop-img-micro-mask"></div>
                        <If condition={isShowToolBar}>
                            <div className="react-crop-toolbar">
                                <i
                                    className="crop-toolbar rotate"
                                    onClick={() => onImageToolBar(OPERATE_TYPE.ROTATE)}
                                />
                                <i
                                    className="crop-toolbar zoom-out "
                                    onClick={() => onImageToolBar(OPERATE_TYPE.ZOOM_OUT)}
                                />
                                <i
                                    className="crop-toolbar zoom-in"
                                    onClick={() => onImageToolBar(OPERATE_TYPE.ZOOM_IN)}
                                />
                            </div>
                        </If>
                    </div>
                </If>

                <div className="crop-btn-area">
                    <Button type="ghost-grey" onClick={onChangeImg}>
                        {lang('更换图片')}
                    </Button>
                    <div className="operate-img">
                        <Button className="operate-img-btn-split" type="ghost-grey" onClick={onCancel}>
                            {lang('取消')}
                        </Button>
                        <Button onClick={confirmHandle} type="primary-blue" className="btn-area btn-submit-area">
                            {lang('确认')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    };
    return render();
};
